import { qhsDebug, qhsSliceSnapshot, isQhsDebug } from '@shared/lib/qhsDebug';

/**
 * Redux debug middleware — auto-logs the auth / table / navigation
 * action stream when the `qhsDebug` localStorage flag is set.
 *
 * Hunting target: the rare "forever loader after auto-logout" bug.
 * We need a faithful timeline of which actions fired and how the
 * relevant state slices moved between them, without grafting
 * console.log calls onto every reducer case.
 *
 * Behaviour:
 *   1. No-op when the flag is off — single localStorage probe per
 *      action, cost is negligible.
 *   2. Filters action types to a relevance regex so noisy slices
 *      (notifications, infoPane toggles, table settings patches
 *      during scrolling, etc.) don't drown the timeline.
 *   3. Logs the action type + payload + a slice snapshot diff.
 *      The diff only carries the fields that actually changed,
 *      so an `ok=true` action that didn't move state still shows
 *      up as a tagged event but with `diff: {}` rather than dumping
 *      the whole snapshot every time.
 *
 * Hooked into the store via `redux_store/middleware/...` and
 * concatenated into the configureStore middleware pipeline. Order
 * relative to navigation/alerts middleware doesn't matter — debug
 * is read-only.
 */

const RELEVANT_ACTION = /^(user|table|navigation|appSettings|alerts|infoPane)\//;

// Credentials never reach the console: the sign-in thunk's argument is the
// form itself ({ email, password }), and support asks people to copy these
// lines. Matched on the exact key name, case-insensitively.
const SECRET_KEY =
  /^(password|confirmPassword|currentPassword|newPassword|oldPassword|secret|code|token|accessToken|refreshToken)$/i;
const REDACTED = '[redacted]';
const MAX_REDACT_DEPTH = 8;

export const redactSecrets = (value, depth = 0) => {
  if (depth > MAX_REDACT_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(item => redactSecrets(item, depth + 1));
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SECRET_KEY.test(key) ? REDACTED : redactSecrets(item, depth + 1),
    ])
  );
};

const computeDiff = (before, after) => {
  const diff = {};
  Object.keys(after).forEach(key => {
    if (before[key] !== after[key]) {
      diff[key] = { from: before[key], to: after[key] };
    }
  });
  return diff;
};

const debugMiddleware = store => next => action => {
  // Fast-path: avoid the snapshot work entirely when the flag is off.
  if (!isQhsDebug()) {
    return next(action);
  }

  if (!action?.type || !RELEVANT_ACTION.test(action.type)) {
    return next(action);
  }

  const before = qhsSliceSnapshot(store.getState());
  const result = next(action);
  const after = qhsSliceSnapshot(store.getState());
  const diff = computeDiff(before, after);

  // Always log lifecycle endings (.pending / .fulfilled / .rejected) —
  // these are the timing-relevant points even if state didn't change.
  // For everything else, log only when state actually moved.
  const isLifecycle = /\.(pending|fulfilled|rejected)$/.test(action.type);
  if (isLifecycle || Object.keys(diff).length) {
    qhsDebug(action.type, {
      payload: redactSecrets(action.payload),
      meta: action.meta?.requestId
        ? { requestId: action.meta.requestId, arg: redactSecrets(action.meta.arg) }
        : undefined,
      error: action.error,
      diff,
    });
  }

  return result;
};

export default debugMiddleware;
